/**
 * Projects Page - Display list of projects and their patient analyses
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { PageContent } from '../components/PageContent';
import { getProjects, type Project } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ExternalLink, Search, Upload } from 'lucide-react';

export function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Extract unique project IDs for dropdown
  const uniqueProjectIds = useMemo(() => {
    const projectIds = [...new Set(projects.map((p) => p.project_id))];
    return projectIds.sort();
  }, [projects]);

  // Filter projects based on selected filters (AND logic)
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchesProject =
        selectedProjectId === 'all' || project.project_id === selectedProjectId;
      const matchesSearch =
        searchQuery.trim() === '' ||
        project.patient_id.toLowerCase().includes(searchQuery.toLowerCase().trim());
      return matchesProject && matchesSearch;
    });
  }, [projects, selectedProjectId, searchQuery]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedProjectId('all');
    setSearchQuery('');
  };

  useEffect(() => {
    async function loadProjects() {
      try {
        const data = await getProjects();
        setProjects(data);
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setLoading(false);
      }
    }

    void loadProjects();
  }, []);

  const handleViewResults = (projectId: string) => {
    void navigate(`/results/${projectId}`);
  };

  return (
    <Layout>
      <PageHeader
        title="Projects"
        description="View and manage patient analyses across projects"
        actions={
          <Button onClick={() => navigate('/upload')}>
            <Upload className="h-4 w-4 mr-2" />
            New Upload
          </Button>
        }
      />
      <PageContent width="xl">
        <Card>
          <CardContent className="p-6">
            {/* Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              {/* Project Filter */}
              <div className="flex flex-col gap-1.5 min-w-[200px]">
                <Label htmlFor="project-filter" className="text-sm text-muted-foreground">
                  Project
                </Label>
                <Select
                  value={selectedProjectId}
                  onValueChange={setSelectedProjectId}
                  disabled={loading}
                >
                  <SelectTrigger id="project-filter" data-testid="project-filter">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {uniqueProjectIds.map((projectId) => (
                      <SelectItem key={projectId} value={projectId}>
                        {projectId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Patient Search */}
              <div className="flex flex-col gap-1.5 flex-1 max-w-xs">
                <Label htmlFor="patient-search" className="text-sm text-muted-foreground">
                  Search Patients
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="patient-search"
                    data-testid="patient-search"
                    type="text"
                    placeholder="Search by Patient ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={loading}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <p className="text-muted-foreground py-12 text-center">Loading projects...</p>
            ) : projects.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No patients found.</p>
                <Button variant="outline" onClick={() => navigate('/upload')}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Your First Patient
                </Button>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No patients match your filters.</p>
                <Button variant="link" onClick={clearFilters}>
                  Clear filters
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient ID</TableHead>
                    <TableHead>Time Points</TableHead>
                    <TableHead>QVT Scores</TableHead>
                    <TableHead className="text-right">Results</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProjects.map((project) => (
                    <TableRow
                      key={project.id}
                      data-project-id={project.id}
                      className="transition-colors hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleViewResults(project.id)}
                    >
                      <TableCell className="font-medium">{project.patient_id}</TableCell>
                      <TableCell>{project.timepoints.join(', ')}</TableCell>
                      <TableCell>
                        {project.qvt_scores
                          ? project.qvt_scores
                              .map((score) => (score !== null ? score.toFixed(3) : 'N/A'))
                              .join(', ')
                          : 'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewResults(project.id)}
                          className="view-results-link transition-all hover:bg-accent hover:text-accent-foreground"
                        >
                          View Results
                          <ExternalLink className="h-3 w-3 ml-2" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </PageContent>
    </Layout>
  );
}
